// Мастер укомплектования: поставить НЕСКОЛЬКИХ людей на один пост объекта.
//
// Тз, раздел 5.2: «выбор объекта и поста, подбор сотрудников из резерва и межвахты с
// фильтрами, период вахты, тариф, роль, подтверждение. Показывает, как изменится
// укомплектованность». До 26.08.2026 такой двери не было: существовал только обратный ход —
// открыть карточку человека и поставить его на объект. Замер того пути: пять нажатий и два
// числа НА ЧЕЛОВЕКА, то есть объект на шесть постов укомплектовывался сорока двумя
// действиями. Здесь те же шесть человек ставятся одним заходом.
//
// ## Тариф один на всю пачку
//
// Он принадлежит ПОСТУ, а не человеку: на одном посту одинаковая работа. Разные ставки
// внутри поста заводятся потом, изменением тарифа по конкретному назначению, — и это
// правильный порядок, потому что такое изменение требует основания и даты.
//
// ## Кого показываем по умолчанию
//
// Свободных: резерв и межвахта. Это те, кого ставят обычно. Занятый человек из списка не
// исчезает совсем — переключатель «показать всех» возвращает его, — потому что у заказчика
// людей переставляют между объектами посреди вахты, и мастер про это предупреждает, а не
// запрещает.
//
// ## Почему панель лежит в разделе «Люди», а открывается из карточки объекта
//
// Её предмет — назначение, а назначения живут здесь: тариф, предупреждения, реестр людей.
// Стопка панелей общая на весь каркас, поэтому раздел «Объекты» открывает её по имени, не
// зная о её устройстве. Обратный порядок — положить панель к объектам — потянул бы в чужой
// раздел поля тарифа, справочник статусов и половину запросов реестра.
//
// Разряд и город стоят В СТРОКЕ, а не отдельными отборами. Отбор по разряду был бы точным
// совпадением, то есть при требовании «4 разряд» спрятал бы пятый и шестой, которые тоже
// годятся. Написанное в строке этой ошибки не делает и не добавляет на экран ещё двух полей.

import { useMemo, useState, type FormEvent } from 'react';

import { RiCheckboxCircleLine, RiErrorWarningLine } from '@remixicon/react';

import type { ApiError } from '@/shared/errors/api-error';
import type { BulkAssignmentOut } from '@/shared/api/endpoints/people';
import { newIdempotencyKey } from '@/shared/api/idempotency';
import {
  useLegalEntitiesQuery,
  usePostsQuery,
  useSiteQuery,
  useSitesQuery,
} from '@/shared/org/queries';
import {
  ДАТА_ПО_НЕДОПИСАНА,
  датаПоНедописана,
  последствиеДатыПо,
} from '@/shared/people/последний-день';
import { can } from '@/shared/rbac/permissions';
import { useAuthStore } from '@/shared/auth/store';
import { countLabel } from '@/shared/text/plural';
import * as Alert from '@/shared/ui/alert';
import * as Button from '@/shared/ui/button';
import { DateField, todayIso } from '@/shared/ui/date-field';
import { ErrorAlert } from '@/shared/ui/error-alert';
import * as Hint from '@/shared/ui/hint';
import { RowCheckbox } from '@/shared/ui/row-checkbox';
import { NO_VALUE, SelectField } from '@/shared/ui/select-field';
import { EmptyState, Skeleton } from '@/shared/ui/screen-state';
import * as Switch from '@/shared/ui/switch';
import { TextField } from '@/shared/ui/text-field';
import { cn } from '@/utils/cn';

import { buildRateValues, EMPTY_RATE_FORM, RateFields } from '../components/RateFields';
import { STATUS_TITLES } from '../dictionaries';
import { useCreateAssignmentsBulkMutation, useEmployeesQuery } from '../queries';

import { STAFF_FROM_REGISTRY } from '../route';

import { взятьВыбор } from './staff-selection';

const EDIT_PERMISSION = 'core.employee.edit';
const PERSON_FORMS = ['человек', 'человека', 'человек'] as const;
/**
 * Родительный падеж: «не хватает 1 человекА», «не хватает 2 человек».
 *
 * Отдельный набор, потому что после «не хватает» слово склоняется иначе, чем при счёте:
 * «2 человекА» верно в «поставлено 2 человека» и неверно в «не хватает 2 человека».
 */
const PERSON_FORMS_GENITIVE = ['человека', 'человек', 'человек'] as const;

export function StaffPostPanel({ id, onClose }: { id: string; onClose: () => void }) {
  const permissions = useAuthStore((state) => state.permissions);
  const allowed = can(permissions, EDIT_PERMISSION);

  const изРеестра = id === STAFF_FROM_REGISTRY;
  const [siteId, setSiteId] = useState(изРеестра ? NO_VALUE : id);
  const объектВыбран = siteId !== NO_VALUE;

  const sites = useSitesQuery({ active: true });
  const site = useSiteQuery(siteId, объектВыбран);
  const posts = usePostsQuery(объектВыбран ? siteId : undefined);
  const legalEntities = useLegalEntitiesQuery();

  const [idempotencyKey, setIdempotencyKey] = useState(newIdempotencyKey);
  const [postId, setPostId] = useState(NO_VALUE);
  const [legalEntityId, setLegalEntityId] = useState(NO_VALUE);
  const [dateFrom, setDateFrom] = useState(todayIso);
  const [dateTo, setDateTo] = useState('');
  // Что стоит в поле «По» на экране. Поле отдаёт наружу пустую строку и на недописанную дату, а
  // пустое «По» значит «без даты окончания»: «30.09.20» молча стало бы бессрочным назначением
  // у всей пачки сразу.
  const [набраноПо, setНабраноПо] = useState('');
  const [rate, setRate] = useState(EMPTY_RATE_FORM);
  const [поиск, setПоиск] = useState('');
  const [всеЛюди, setВсеЛюди] = useState(false);
  const [выбраны, setВыбраны] = useState<readonly string[]>(взятьВыбор);
  const [ошибки, setОшибки] = useState<Record<string, string>>({});
  const [запрет, setЗапрет] = useState<string | null>(null);
  const [итог, setИтог] = useState<BulkAssignmentOut | null>(null);

  const mutation = useCreateAssignmentsBulkMutation(idempotencyKey);

  // Свободные — это два статуса, а отбор на сервере принимает один. Два запроса вместо
  // одного и склейка здесь: заводить ради этого список статусов в контракте значило бы
  // менять общий обмен под один экран.
  const резерв = useEmployeesQuery({ status: 'reserve', q: поиск || undefined, limit: 200 });
  const межвахта = useEmployeesQuery({
    status: 'between_shifts',
    q: поиск || undefined,
    limit: 200,
  });
  const всеСтатусы = useEmployeesQuery({ q: поиск || undefined, limit: 200 });

  const кандидаты = useMemo(() => {
    const источник = всеЛюди
      ? (всеСтатусы.data?.items ?? [])
      : [...(резерв.data?.items ?? []), ...(межвахта.data?.items ?? [])];
    // Уволенных не предлагаем никогда: поставить уволенного — это не решение человека, а
    // промах по строке.
    return источник
      .filter((человек) => человек.status !== 'fired')
      .sort((левый, правый) => левый.last_name.localeCompare(правый.last_name, 'ru'));
  }, [всеЛюди, всеСтатусы.data, резерв.data, межвахта.data]);

  const едутКандидаты = всеЛюди ? всеСтатусы.isPending : резерв.isPending || межвахта.isPending;

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!allowed) {
      setЗапрет(
        'Ставить людей на объект может тот, у кого есть право на правку карточек. Попросите администратора выдать его.',
      );
      return;
    }
    if (mutation.isPending) {
      setЗапрет('Назначения уже создаются, ждём ответ сервера. Второй раз не отправляем.');
      return;
    }

    const next: Record<string, string> = {};
    if (!объектВыбран) {
      next.site = 'Выберите объект: пост принадлежит объекту, без него список постов пуст.';
    }
    if (postId === NO_VALUE) {
      next.post = 'Выберите пост: от него зависят часы, разряд и норматив.';
    }
    if (legalEntityId === NO_VALUE) {
      next.legal_entity = 'Выберите юрлицо: от него зависит, в чью ведомость попадут люди.';
    }
    if (dateFrom === '') {
      next.date_from = 'Укажите, с какого дня люди заступают.';
    }
    if (dateTo !== '' && dateTo < dateFrom) {
      next.date_from = 'Последний день раньше первого: проверьте даты.';
    }
    if (датаПоНедописана(dateTo, набраноПо)) {
      next.date_to = ДАТА_ПО_НЕДОПИСАНА;
    }
    if (выбраны.length === 0) {
      next.people = 'Отметьте хотя бы одного человека: ставить пока некого.';
    }

    const rateValues = buildRateValues(rate);
    if (!rateValues.ok) {
      Object.assign(next, rateValues.errors);
    }
    if (Object.keys(next).length > 0 || !rateValues.ok) {
      setОшибки(next);
      return;
    }

    setОшибки({});
    setЗапрет(null);
    mutation.mutate(
      {
        employee_ids: [...выбраны],
        post_id: postId,
        legal_entity_id: legalEntityId,
        date_from: dateFrom,
        date_to: dateTo === '' ? null : dateTo,
        ...rateValues.values,
      },
      {
        onSuccess: (ответ) => {
          // Панель НЕ закрывается сама: в ответе приехали и предупреждения, и те, кого
          // поставить не удалось. Закрыть её сейчас значило бы спрятать именно то, ради чего
          // человек сюда шёл.
          setИтог(ответ);
          setIdempotencyKey(newIdempotencyKey());
        },
      },
    );
  }

  if (итог !== null) {
    return (
      <ИтогПостановки
        итог={итог}
        onClose={onClose}
        onЕщё={() => {
          setИтог(null);
          setВыбраны([]);
        }}
      />
    );
  }

  return (
    <form onSubmit={submit} noValidate className='flex flex-col gap-3'>
      {объектВыбран && (
        <p className='text-paragraph-xs text-text-sub-600'>
          {site.data?.name ?? 'загружаем объект…'}
        </p>
      )}

      {!allowed && (
        <Alert.Root variant='light' status='warning' size='small' role='status'>
          <Alert.Icon as={RiErrorWarningLine} />
          <span>
            Права на правку карточек у вас нет: форму можно заполнить, но назначения не сохранятся.
            Право выдаёт администратор.
          </span>
        </Alert.Root>
      )}

      {изРеестра && (
        <SelectField
          id='staff-site'
          label='Объект'
          required
          value={siteId}
          onChange={(значение) => {
            setSiteId(значение);
            // Пост принадлежит объекту: сменили объект — прежний пост чужой.
            setPostId(NO_VALUE);
          }}
          options={(sites.data?.items ?? []).map((объект) => ({
            value: объект.id,
            label: объект.name,
          }))}
          placeholder='Выберите объект'
          pending={sites.isPending}
          failed={sites.isError}
          failedError={sites.error as ApiError | null}
          emptyNote='Действующих объектов нет: ставить людей некуда. Заведите объект в разделе «Объекты».'
          error={ошибки.site}
        />
      )}

      <SelectField
        id='staff-post'
        label='Пост'
        required
        value={postId}
        onChange={setPostId}
        options={(posts.data?.items ?? []).map((пост) => ({
          value: пост.id,
          label:
            пост.required_guard_category === null
              ? пост.name
              : `${пост.name} · ${пост.required_guard_category} разряд`,
        }))}
        placeholder={объектВыбран ? 'Выберите пост' : 'Сначала выберите объект'}
        pending={объектВыбран && posts.isPending}
        failed={posts.isError}
        failedError={posts.error as ApiError | null}
        emptyNote={
          объектВыбран
            ? 'У объекта нет ни одного поста: ставить не на что. Заведите пост на вкладке «Посты».'
            : undefined
        }
        error={ошибки.post}
      />

      <div className='flex flex-col gap-2'>
        <div className='flex items-baseline justify-between gap-2'>
          <span className='text-label-sm text-text-strong-950'>Кого ставим</span>
          <span className='shrink-0 text-paragraph-xs text-text-sub-600'>
            выбрано {выбраны.length}
          </span>
        </div>

        <TextField
          id='staff-search'
          label='Поиск по фамилии'
          value={поиск}
          onChange={setПоиск}
          placeholder='Старцев'
        />

        <label className='flex items-center justify-between gap-3 text-paragraph-sm text-text-sub-600'>
          Показать всех, не только свободных
          <Switch.Root checked={всеЛюди} onCheckedChange={setВсеЛюди} />
        </label>

        <div className='max-h-64 overflow-y-auto rounded-lg bg-bg-weak-50'>
          {едутКандидаты ? (
            <div className='flex flex-col gap-2 p-3'>
              <Skeleton className='h-9 w-full' />
              <Skeleton className='h-9 w-full' />
              <Skeleton className='h-9 w-full' />
            </div>
          ) : кандидаты.length === 0 ? (
            <div className='p-3'>
              <EmptyState
                title={всеЛюди ? 'Никого не нашлось' : 'Свободных нет'}
                description={
                  всеЛюди
                    ? 'По этой фамилии в реестре никого нет. Проверьте написание.'
                    : 'Ни в резерве, ни на межвахте сейчас никого. Включите «показать всех»: занятого человека поставить можно, мастер про это предупредит.'
                }
              />
            </div>
          ) : (
            <ul className='flex flex-col divide-y divide-stroke-soft-200'>
              {кандидаты.map((человек) => {
                const отмечен = выбраны.includes(человек.id);
                return (
                  <li key={человек.id} className='flex items-center gap-2 px-2 py-1.5'>
                    <RowCheckbox
                      id={`staff-${человек.id}`}
                      label={`Выбрать: ${человек.display_name}`}
                      checked={отмечен}
                      onCheckedChange={(next) =>
                        setВыбраны((прежние) =>
                          next
                            ? [...прежние, человек.id]
                            : прежние.filter((строка) => строка !== человек.id),
                        )
                      }
                    />
                    <span className='flex min-w-0 flex-1 flex-col'>
                      <span className='truncate text-label-sm text-text-strong-950'>
                        {человек.display_name}
                      </span>
                      <span className='truncate text-paragraph-xs text-text-sub-600'>
                        {STATUS_TITLES[человек.status]}
                        {человек.city !== null && <> · {человек.city}</>}
                        {человек.guard_category !== null && <> · {человек.guard_category} разряд</>}
                        {человек.has_debt && <span className='text-warning-base'> · долг</span>}
                      </span>
                    </span>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
        {ошибки.people !== undefined && (
          <Hint.Root hasError>
            <Hint.Icon as={RiErrorWarningLine} />
            {ошибки.people}
          </Hint.Root>
        )}
      </div>

      <SelectField
        id='staff-legal-entity'
        label='Юрлицо'
        required
        value={legalEntityId}
        onChange={setLegalEntityId}
        options={(legalEntities.data?.items ?? []).map((строка) => ({
          value: строка.id,
          label: строка.short_name ?? строка.name,
        }))}
        placeholder='Выберите юрлицо'
        pending={legalEntities.isPending}
        failed={legalEntities.isError}
        failedError={legalEntities.error as ApiError | null}
        emptyNote='Юрлиц в справочнике нет. Их заводит администратор в настройках системы.'
        error={ошибки.legal_entity}
        hint='От юрлица зависит, в чью ведомость попадут люди.'
      />

      <DateField
        id='staff-date-from'
        label='Заступают с'
        required
        value={dateFrom}
        onChange={setDateFrom}
        error={ошибки.date_from}
      />
      {/* Обёртка ловит набор в поле «По»: само поле отдаёт наружу только целую дату. */}
      <div onInput={(event) => setНабраноПо((event.target as HTMLInputElement).value)}>
        <DateField
          id='staff-date-to'
          label='По'
          value={dateTo}
          onChange={setDateTo}
          error={ошибки.date_to}
          // Последний день включительный: под полем сказано, с какого дня людей не будет в табеле.
          hint={
            последствиеДатыПо(dateTo, 'люди поста') ??
            'Можно оставить пустым: назначения будут считаться живыми, пока их не закроют.'
          }
        />
      </div>

      {/* Тариф один на всю пачку: он принадлежит посту, а не человеку. */}
      <RateFields idPrefix='staff' value={rate} onChange={setRate} errors={ошибки} />

      {mutation.isError && <ErrorAlert error={mutation.error} />}

      {запрет !== null && (
        <Hint.Root hasError>
          <Hint.Icon as={RiErrorWarningLine} />
          {запрет}
        </Hint.Root>
      )}

      <div className='flex items-center gap-2 pt-1'>
        <Button.Root type='submit' variant='primary' mode='filled' size='small'>
          {mutation.isPending
            ? 'Ставим на пост…'
            : выбраны.length === 0
              ? 'Поставить на пост'
              : `Поставить ${countLabel(выбраны.length, PERSON_FORMS)}`}
        </Button.Root>
        <Button.Root type='button' variant='neutral' mode='stroke' size='small' onClick={onClose}>
          Отмена
        </Button.Root>
      </div>
    </form>
  );
}

/** Итог пачки: сколько встало, кто не встал и почему, как изменилась укомплектованность. */
function ИтогПостановки({
  итог,
  onClose,
  onЕщё,
}: {
  итог: BulkAssignmentOut;
  onClose: () => void;
  onЕщё: () => void;
}) {
  const непоставленные = итог.rows.filter((строка) => строка.assignment_id === null);
  const спредупреждением = итог.rows.filter(
    (строка) => строка.assignment_id !== null && строка.warnings.length > 0,
  );
  const { staffing } = итог;
  const нехватка = staffing.required - staffing.assigned_after;

  return (
    <div className='flex flex-col gap-3'>
      <Alert.Root
        variant='light'
        status={итог.skipped === 0 ? 'success' : 'warning'}
        size='small'
        role='status'
      >
        <Alert.Icon as={итог.skipped === 0 ? RiCheckboxCircleLine : RiErrorWarningLine} />
        <span>
          Поставлено {countLabel(итог.created, PERSON_FORMS)}
          {итог.skipped > 0 && <>, не поставлено {итог.skipped}</>}.
        </span>
      </Alert.Root>

      <p className='text-paragraph-sm text-text-sub-600'>
        {/* Укомплектованность словами: «5/6» человек читает как дробь, а не как ответ. */}
        {нехватка > 0
          ? `На объекте назначено ${staffing.assigned_after} из ${staffing.required} по постам: не хватает ${countLabel(нехватка, PERSON_FORMS_GENITIVE)}.`
          : нехватка === 0
            ? `Объект укомплектован: назначено ровно ${countLabel(staffing.required, PERSON_FORMS)}, сколько требуется постами.`
            : `Назначено ${staffing.assigned_after} при потребности ${staffing.required}: на ${countLabel(-нехватка, PERSON_FORMS_GENITIVE)} больше, чем требуется постами.`}
      </p>

      {непоставленные.length > 0 && (
        <div className='flex flex-col gap-1.5 rounded-lg bg-bg-weak-50 p-3'>
          <span className='text-label-sm text-text-strong-950'>Не встали</span>
          <ul className='flex flex-col gap-1 text-paragraph-xs text-text-sub-600'>
            {непоставленные.map((строка) => (
              <li key={строка.employee_id}>
                <span className='text-text-strong-950'>{строка.employee_name}</span> —{' '}
                {строка.skipped_reason}
              </li>
            ))}
          </ul>
        </div>
      )}

      {спредупреждением.length > 0 && (
        <div className='flex flex-col gap-1.5 rounded-lg bg-bg-weak-50 p-3'>
          <span className='text-label-sm text-text-strong-950'>
            Встали, но есть на что посмотреть
          </span>
          <ul className='flex flex-col gap-1 text-paragraph-xs text-text-sub-600'>
            {спредупреждением.map((строка) => (
              <li key={строка.employee_id}>
                <span className='text-text-strong-950'>{строка.employee_name}</span> —{' '}
                {строка.warnings.map((предупреждение) => предупреждение.message).join('; ')}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className={cn('flex items-center gap-2 pt-1')}>
        <Button.Root type='button' variant='primary' mode='filled' size='small' onClick={onClose}>
          Готово
        </Button.Root>
        <Button.Root type='button' variant='neutral' mode='stroke' size='small' onClick={onЕщё}>
          Поставить ещё
        </Button.Root>
      </div>
    </div>
  );
}
